import { ChevronDown } from "@icons";
import Tooltip from "@src/components/tooltip";
import classNames from "classnames";
import { ReactNode, useId, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

interface MenuItemBaseProp {
  label: string;
  icon: ReactNode;
  minimized?: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

interface MenuItemProps extends MenuItemBaseProp {
  to?: string;
  onClick?: null | (() => void);
}

interface MenuItemWithSubmenuProps extends MenuItemBaseProp {
  submenu: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
}

export function MenuItem(props: MenuItemProps | MenuItemWithSubmenuProps) {
  const {
    label,
    icon,
    minimized = false,
    setMobileMenuOpen = () => {},
  } = props;
  const { submenu } = props as MenuItemWithSubmenuProps;
  const { to, onClick } = props as MenuItemProps;

  const location = useLocation();
  const isOnPage =
    (to && location.pathname.startsWith(to)) ||
    (submenu?.length > 0 &&
      submenu.some((item) => item.key === location.pathname));

  const [isOpen, setIsOpen] = useState(false);
  const submenuId = useId();

  const content = (
    <div
      className={classNames(
        "w-full min-h-target py-1 px-2 flex my-0 mx-auto",
        "group-focus/menuitem:bg-page group-focus/menuitem:text-primary",
        "group-hover/menuitem:bg-page group-hover/menuitem:text-primary",
        "rounded flex items-center",
        { "bg-page text-primary": isOnPage },
      )}
    >
      <div
        className={classNames("w-6 h-6 flex justify-center items-center", {
          "mr-2": !minimized,
        })}
      >
        <Tooltip label={label}>
          <div
            className={classNames(
              "md:text-muted",
              { "text-muted": props.minimized },
              { "text-on-primary": !props.minimized && !isOnPage },
              { "text-muted": isOnPage },
            )}
          >
            {icon}
          </div>
        </Tooltip>
      </div>
      {minimized ? <span className="sr-only">{label}</span> : label}
      {!!submenu && !minimized && submenu.length > 0 && (
        <div
          className={classNames(
            "ml-auto mr-2 transition-transform duration-300",
            { "rotate-180 h-fit": isOpen },
          )}
        >
          <ChevronDown size={16} />
        </div>
      )}
    </div>
  );

  if (submenu && submenu.length > 0) {
    return (
      <div>
        <button
          type="button"
          className="w-full"
          aria-expanded={isOpen}
          aria-controls={submenuId}
          onClick={() => setIsOpen(!isOpen)}
        >
          {content}
        </button>
        <div
          id={submenuId}
          className={classNames("overflow-hidden transition-all duration-400", {
            "max-h-0 invisible": !isOpen,
            "max-h-screen": isOpen,
          })}
        >
          {submenu.map((item) => (
            <item.component
              key={item.key}
              minimized={minimized}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          ))}
        </div>
      </div>
    );
  }

  if (onClick) {
    return (
      <button className="block w-full" onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <NavLink
      to={to as string}
      className="nav-menu-link group/menuitem block outline-none ring-0 hover:cursor-pointer"
      onClick={() => setMobileMenuOpen(false)}
    >
      {content}
    </NavLink>
  );
}
